// FILE: src/components/AdminAttendanceReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

function toJsDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate(); // Firestore Timestamp
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  return new Date(value);
}

function formatDateTime(val) {
  const d = toJsDate(val);
  if (!d) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ⭐ "AI" logic implemented on the frontend
function generateAiInsights(summary, events) {
  if (!events.length || summary.totalCheckins === 0) {
    return (
      "No attendance data is available yet. " +
      "Once members start checking in for Sunday services and events, " +
      "this panel will highlight trends and suggestions automatically."
    );
  }

  const sundayEvents = events.filter((e) => e.isSundayService);
  const otherEvents = events.filter((e) => !e.isSundayService);

  const topByCheckins = [...events].sort(
    (a, b) => b.totalCheckins - a.totalCheckins
  );
  const bestEvent = topByCheckins[0];

  // Simple trend: compare first half vs second half averages
  const sortedByDate = [...events].sort(
    (a, b) => (toJsDate(a.startAt) || 0) - (toJsDate(b.startAt) || 0)
  );
  const mid = Math.floor(sortedByDate.length / 2) || 1;
  const firstHalf = sortedByDate.slice(0, mid);
  const secondHalf = sortedByDate.slice(mid);

  const avg = (arr) =>
    arr.length
      ? arr.reduce((sum, e) => sum + e.totalCheckins, 0) / arr.length
      : 0;

  const firstAvg = avg(firstHalf);
  const secondAvg = avg(secondHalf);

  let trendSentence = "";
  if (secondAvg > firstAvg + 2) {
    trendSentence =
      "Attendance is trending upwards in recent events compared to earlier ones, which is a positive sign of engagement. ";
  } else if (secondAvg + 2 < firstAvg) {
    trendSentence =
      "Recent events are seeing slightly lower attendance than earlier ones, suggesting a need to re-engage members. ";
  } else {
    trendSentence =
      "Attendance has been fairly stable over time, with no major spikes or drops. ";
  }

  const sundayShare =
    summary.totalCheckins > 0
      ? Math.round(
          (summary.sundayCheckins / summary.totalCheckins) * 100
        )
      : 0;

  let sundaySentence = "";
  if (sundayShare >= 60) {
    sundaySentence =
      `Most attendance (${sundayShare}% of check-ins) happens during Sunday services, ` +
      "which shows that the main worship gathering is a strong anchor for the church. ";
  } else if (sundayShare >= 30) {
    sundaySentence =
      `Sunday services account for about ${sundayShare}% of check-ins, ` +
      "while other events also play an important role in keeping people connected. ";
  } else {
    sundaySentence =
      `Only about ${sundayShare}% of check-ins are on Sunday services, ` +
      "which may mean that midweek or special events are currently more attractive to members. ";
  }

  let bestEventSentence = "";
  if (bestEvent) {
    bestEventSentence =
      `The best-attended event so far is “${bestEvent.title}” ` +
      `with ${bestEvent.totalCheckins} total check-ins ` +
      `and ${bestEvent.uniqueAttendees} unique attendees. `;
  }

  const suggestions = [];
  if (otherEvents.length > 0) {
    suggestions.push(
      "Consider sending reminders or follow-up messages for non-Sunday events to grow engagement there."
    );
  }
  if (sundayEvents.length > 0 && sundayShare < 70) {
    suggestions.push(
      "Highlight Sunday services during announcements and in digital communication to strengthen core attendance."
    );
  }
  if (summary.totalCheckins > 20) {
    suggestions.push(
      "You could also identify regular attendees and invite them to serve or join small groups, turning attendance into deeper discipleship."
    );
  }

  const suggestionsText =
    suggestions.length > 0
      ? "Suggestions: " + suggestions.join(" ")
      : "At this stage, keep monitoring attendance and experimenting with event formats and communication.";

  return (
    `Here is an automated summary of current attendance patterns.\n\n` +
    trendSentence +
    sundaySentence +
    bestEventSentence +
    suggestionsText
  );
}

export default function AdminAttendanceReport() {
  const { role } = useAuth();
  const [events, setEvents] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [aiInsights, setAiInsights] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const evSnap = await getDocs(
          query(collection(db, "events"), orderBy("startAt", "asc"))
        );
        const eventList = evSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const chkSnap = await getDocs(
          query(collection(db, "eventCheckins"), orderBy("checkedAt", "asc"))
        );
        const chkList = chkSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setEvents(eventList);
        setCheckins(chkList);
      } catch (err) {
        console.error("Error loading attendance data:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const eventMap = useMemo(() => {
    const m = new Map();
    events.forEach((e) => m.set(e.id, e));
    return m;
  }, [events]);

  const eventStats = useMemo(() => {
    const stats = {};
    for (const chk of checkins) {
      const eid = chk.eventId;
      if (!eid) continue;
      if (!stats[eid]) {
        stats[eid] = {
          eventId: eid,
          count: 0,
          attendees: new Set(),
        };
      }
      stats[eid].count += 1;
      if (chk.uid) stats[eid].attendees.add(chk.uid);
    }

    return Object.values(stats).map((s) => {
      const ev = eventMap.get(s.eventId);
      return {
        eventId: s.eventId,
        title: ev?.title || "Unknown event",
        isSundayService: !!ev?.isSundayService,
        startAt: ev?.startAt,
        totalCheckins: s.count,
        uniqueAttendees: s.attendees.size,
      };
    });
  }, [checkins, eventMap]);

  const overallStats = useMemo(() => {
    const totalCheckins = checkins.length;

    // 🔧 FIX: derive Sunday check-ins from the event info,
    // not from a field on the check-in document
    const sundayCheckins = checkins.filter((c) => {
      const ev = eventMap.get(c.eventId);
      return ev?.isSundayService;
    }).length;

    const otherCheckins = totalCheckins - sundayCheckins;

    const sundayEvents = eventStats.filter((e) => e.isSundayService);
    const otherEvents = eventStats.filter((e) => !e.isSundayService);

    return {
      totalCheckins,
      sundayCheckins,
      otherCheckins,
      totalEvents: events.length,
      sundayEventsCount: sundayEvents.length,
      otherEventsCount: otherEvents.length,
    };
  }, [checkins, eventStats, events.length, eventMap]);

  const topEvents = useMemo(() => {
    return [...eventStats]
      .sort((a, b) => b.totalCheckins - a.totalCheckins)
      .slice(0, 5);
  }, [eventStats]);

  const handleGenerateAiInsights = () => {
    setAiLoading(true);
    // Simulate "thinking" a bit for the demo feel
    setTimeout(() => {
      const text = generateAiInsights(overallStats, eventStats);
      setAiInsights(text);
      setAiLoading(false);
    }, 600);
  };

  if (role !== "admin") {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">
          You do not have permission to view this report.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">Loading attendance analytics…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Event Attendance Report</h2>
          <p className="text-sm text-gray-600">
            AI-generated insights based on Sunday Service and event check-ins.
          </p>
        </div>
        <button
          onClick={handleGenerateAiInsights}
          disabled={aiLoading || loading}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-60 hover:bg-indigo-700"
        >
          {aiLoading ? "Analyzing…" : "Generate AI Insights"}
        </button>
      </div>

      {/* High-level stats */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">Total Check-ins</div>
          <div className="text-2xl font-semibold">
            {overallStats.totalCheckins}
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">Sunday Service Check-ins</div>
          <div className="text-2xl font-semibold">
            {overallStats.sundayCheckins}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Across {overallStats.sundayEventsCount} Sunday events
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">Other Event Check-ins</div>
          <div className="text-2xl font-semibold">
            {overallStats.otherCheckins}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Across {overallStats.otherEventsCount} events
          </div>
        </div>
      </section>

      {/* Top events */}
      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Top Attended Events</h3>
        {topEvents.length === 0 ? (
          <p className="text-sm text-gray-500">
            No check-ins recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 bg-white rounded-lg overflow-hidden">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border px-3 py-2 text-left">Event</th>
                  <th className="border px-3 py-2 text-left">Type</th>
                  <th className="border px-3 py-2 text-left">Date</th>
                  <th className="border px-3 py-2 text-right">
                    Total Check-ins
                  </th>
                  <th className="border px-3 py-2 text-right">
                    Unique Attendees
                  </th>
                </tr>
              </thead>
              <tbody>
                {topEvents.map((ev) => (
                  <tr key={ev.eventId} className="hover:bg-slate-50">
                    <td className="border px-3 py-2">{ev.title}</td>
                    <td className="border px-3 py-2">
                      {ev.isSundayService ? "Sunday Service" : "Event"}
                    </td>
                    <td className="border px-3 py-2">
                      {formatDateTime(ev.startAt)}
                    </td>
                    <td className="border px-3 py-2 text-right">
                      {ev.totalCheckins}
                    </td>
                    <td className="border px-3 py-2 text-right">
                      {ev.uniqueAttendees}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* AI insights */}
      <section className="space-y-2">
        <h3 className="text-lg font-semibold">AI Insights</h3>
        {aiLoading && (
          <p className="text-sm text-gray-500">
            Analyzing attendance patterns…
          </p>
        )}
        {!aiLoading && !aiInsights && (
          <p className="text-sm text-gray-500">
            Click “Generate AI Insights” to automatically summarize trends and
            suggestions based on current attendance data.
          </p>
        )}
        {aiInsights && (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-600 whitespace-pre-line">
            {aiInsights}
          </div>
        )}
      </section>
    </div>
  );
}

// 👉 named export for your unit tests
export { generateAiInsights };
